import { mainAdminSplitApi } from 'redux/store/mainSplitApi'
import { GradingInstruction, GradingInstructionDTO, type QueryParameters, type QueryResult } from 'types'
import { loadToken } from 'utils/OktaToken'

const instructionsApi = mainAdminSplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // Partial allows the query parameters to be optional, in which case they
    // are omitted from the request URI (which the backend knows how to handle)
    searchGradingInstructions: builder.query<QueryResult<GradingInstruction>, Partial<QueryParameters<GradingInstruction>>>({
      query: (parameters) => {
        return {
          url: '/instructions/grading',
          params: parameters
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [...result.results.map(({ id }) => ({ type: 'GradingInstructions' as const, id: id != null ? id : undefined })), 'GradingInstructions']
          : ['GradingInstructions'],
      transformResponse: (response: QueryResult<GradingInstructionDTO>) => ({
        ...response,
        results: response.results.map((instruction) => new GradingInstruction(instruction))
      })
    }),
    createGradingInstructions: builder.mutation<GradingInstruction, GradingInstruction>({
      query: (instruction) => ({
        url: '/instructions/grading',
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: { ...new GradingInstructionDTO(instruction) }
      }),
      invalidatesTags: (result, error, arg) => error ? [] : ['GradingInstructions'],
      transformResponse: (response: GradingInstructionDTO) => new GradingInstruction(response)
    }),
    updateGradingInstructions: builder.mutation<GradingInstruction, GradingInstruction>({
      query: (instruction) => ({
        url: '/instructions/grading',
        headers: { 'Content-Type': 'application/json' },
        method: 'PUT',
        body: { ...new GradingInstructionDTO(instruction) }
      }),
      invalidatesTags: (result, error, arg) => {
        if (error) return []
        const id = arg.id != null ? arg.id : undefined
        return [{ type: 'GradingInstructions', id }]
      },
      transformResponse: (response: GradingInstructionDTO) => new GradingInstruction(response)
    }),
    deleteGradingInstructions: builder.mutation<void, number>({
      query: (id) => ({
        url: `/instructions/grading/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'GradingInstructions', id }]
    })
  })
})

export const { useSearchGradingInstructionsQuery, useCreateGradingInstructionsMutation, useUpdateGradingInstructionsMutation, useDeleteGradingInstructionsMutation } = instructionsApi

export async function exportGradingInstructionsCsv (params?: Record<string, string>): Promise<void> {
  const baseUrl = (process.env.REACT_APP_ADMIN_API_URL ?? '').replace(/\/$/, '')
  const token = loadToken()
  const query = params ? new URLSearchParams(params).toString() : ''
  const url = `${baseUrl}/instructions/grading/export${query ? `?${query}` : ''}`

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  })

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`)
  }

  const blob = await response.blob()
  const timestamp = Date.now()
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = `Special Instructions ${timestamp}.csv`
  link.click()
  window.URL.revokeObjectURL(link.href)
}
